import { useState, useEffect, useRef } from "react";
import { Dialog } from "@headlessui/react";
import * as pdfjsLib from "pdfjs-dist";
import pdfjsWorker from "pdfjs-dist/build/pdf.worker.entry";
import { useNavigate, useParams } from "react-router-dom";
import {
  Users,
  FileText,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ImageUpscale,
  Copy,
} from "lucide-react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import { GET, POST } from "../../service/CRUDService";
import CfLoader from "../../common/CfLoader/CfLoader";
import Toast from "../../common/Toast/Toast";
import EntityConstants from "../../configs/EntityConstants";
import useNavigationStore from "../../store/navigationStore";
import QrEditModal from "../../common/QrEditModal/QrEditModal";
import { useTranslation } from "react-i18next";

export default function IssuanceSummaryPage() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isQrEditModalOpen, setIsQrEditModalOpen] = useState(false);
  const [previewRecipient, setPreviewRecipient] = useState(null);
  const [loading, setLoading] = useState(true);
  const isIssuingRef = useRef(false);
  const [summaryData, setSummaryData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [certificateName, setCertificateName] = useState("");

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, navigate]);

  const [qrPosition, setQrPosition] = useState({ x: 50, y: 50 });
  const [qrSize, setQrSize] = useState({ width: 110, height: 110 });

  const certificateRef = useRef(null);
  const pdfCanvasRef = useRef(null);
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

  const handlePreviewCertificate = async (recipient) => {
    if (!recipient.is_legacy_file_present) return;

    setPreviewRecipient(recipient);
    setIsPreviewModalOpen(true);

    if (recipient.legacy_file_url) {
      try {
        const loadingTask = pdfjsLib.getDocument(recipient.legacy_file_url);
        const pdf = await loadingTask.promise;

        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 1.0 });

        const canvas = pdfCanvasRef.current;
        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        await page.render(renderContext).promise;
      } catch (error) {
        console.error("Error rendering PDF:", error);
      }
    }
  };

  const handleOpenQrEditModal = () => {
    setIsQrEditModalOpen(true);
  };

  const handleIssueCertificates = async () => {
    if (isIssuingRef.current) return;
    isIssuingRef.current = true;
    setIsConfirmModalOpen(false);
    setLoading(true);

    let didNavigate = false;
    try {
      const payload = {
        data: summaryData.validated_data
          .filter(
            (recipient) =>
              recipient.is_legacy_file_present &&
              recipient.is_duplicate !== true,
          )
          .map((recipient) => {
            const recipientData = {};

            Object.keys(recipient).forEach((key) => {
              if (
                key !== "is_legacy_file_present" &&
                key !== "file_name" &&
                key !== "legacy_file_url"
              ) {
                recipientData[key] = recipient[key];
              }
            });

            recipientData.legacy_document = recipient.legacy_file_url;

            return recipientData;
          }),
      };

      const response = await POST(
        `${EntityConstants.POST_LEGACY_ISSUANCE}?credential_template=${params.issuer_credential_template_id}&legacy_transaction_id=${params.legacyTransactionId}`,
        payload,
      );

      const data = await response.json();

      if (response.ok) {
        didNavigate = true;
        navigate(
          `/issuer/workspace/batches/summary/${data.credential_template_id}/${data.issuer_credential_template_id}?templateType=${data.template_type}`,
          {
            state: {
              transaction_id: data.transaction_id,
              programme: data.programme_name,
              batch: data.batch_name,
            },
          },
        );
      } else {
        throw new Error(
          data.message || t("workspace:failed_issue_certificates"),
        );
      }
    } catch (error) {
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: error.message,
      });
    } finally {
      if (!didNavigate) {
        isIssuingRef.current = false;
        setLoading(false);
      }
      setIsConfirmModalOpen(false);
    }
  };

  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        const response = await GET(
          `${EntityConstants.GET_VIEW_SUMMARY}?legacy_transction_log_id=${params.legacyTransactionId}`,
        );

        const data = await response.json();

        if (response.ok) {
          setSummaryData(data);
          setCertificateName(data.legacy_transcation_data.certificate_name);
        } else {
          throw new Error(
            data.message || t("workspace:failed_fetch_summary_data"),
          );
        }
      } catch (error) {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: error.message,
        });
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchSummaryData();
  }, [params.legacyTransactionId, navigate]);

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };
  const handleWorkspaceClick = () => {
    navigate("/issuer/workspace");
  };

  const handlePendingPageClick = () => {
    const credentialTemplateId =
      summaryData?.legacy_transcation_data?.credential_template_id;
    const issuerCredentialTemplateId =
      summaryData?.legacy_transcation_data?.issuer_credential_template_id;
    if (credentialTemplateId && issuerCredentialTemplateId) {
      const params = new URLSearchParams({
        templateType: "Legacy",
      });
      const navigationUrl = `/issuer/workspace/batches/summary/${credentialTemplateId}/${issuerCredentialTemplateId}?${params.toString()}`;
      navigate(navigationUrl);
    } else {
      navigate(-1);
    }
  };

  const handleKeyDown = (event, handler) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handler();
    }
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = summaryData?.validated_data?.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(
    (summaryData?.validated_data?.length || 0) / itemsPerPage,
  );

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const firstValidPdfUrl = summaryData?.validated_data?.find(
    (recipient) => recipient.is_legacy_file_present,
  )?.legacy_file_url;

  if (!currentProgramme || !currentBatch || loading) {
    return (
      <div
        className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center"
        id={pageRootTestIds.legacyIssuance}
      >
        <CfLoader />
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <div className="bg-cfSecondary p-6 rounded-lg shadow-md">
          <p className="text-lg text-gray-700">
            {t("workspace:no_summary_data_available")}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-cfPrimaryButton text-cfSecondary rounded-md hover:bg-cfPrimaryDark"
          >
            {t("workspace:go_back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10">
      <div>
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
        >
          <div
            className={`flex flex-col lg:flex-row items-start lg:items-center min-h-[4rem] justify-between w-full py-2`}
          >
            <div className="min-w-0 w-full">
              <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                <h2 className="text-lg font-medium flex items-center justify-start min-h-[2.5rem] lg:min-h-[4rem] w-full min-w-0">
                  <div className="flex items-center gap-2 text-sm text-cfTextBreadcrumbDisabled truncate min-w-0 max-w-full">
                    <button
                      onClick={handleWorkspaceClick}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleWorkspaceClick();
                        }
                      }}
                      className="hover:text-gray-800 cursor-pointer focus:outline-none"
                    >
                      {t("workspace:workspace")}
                    </button>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                    <button
                      onClick={handleProgrammeClick}
                      onKeyDown={(e) => handleKeyDown(e, handleProgrammeClick)}
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer focus:outline-none breadcrumb-item"
                      tabIndex={0}
                      aria-label={`${t("workspace:navigate_to")} ${
                        currentProgramme?.programme_name ||
                        t("workspace:programme")
                      }`}
                      title={
                        currentProgramme?.programme_name ||
                        t("workspace:programme")
                      }
                    >
                      {currentProgramme?.programme_name ||
                        t("workspace:programme")}
                    </button>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                    <button
                      onClick={handleBatchClick}
                      onKeyDown={(e) => handleKeyDown(e, handleBatchClick)}
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer focus:outline-none breadcrumb-item"
                      tabIndex={0}
                      aria-label={`${t("workspace:navigate_to")} ${
                        currentBatch?.batch_name || t("workspace:batch")
                      }`}
                      title={currentBatch?.batch_name || t("workspace:batch")}
                    >
                      {currentBatch?.batch_name || t("workspace:batch")}
                    </button>

                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-cfTextBreadcrumbDisabled" />
                    <button
                      onClick={handlePendingPageClick}
                      onKeyDown={(e) =>
                        handleKeyDown(e, handlePendingPageClick)
                      }
                      className="hover:text-cfTextBreadcrumbActive cursor-pointer focus:outline-none breadcrumb-item"
                      tabIndex={0}
                      aria-label={`${t("workspace:navigate_to")} ${t(
                        "workspace:summary",
                      )}`}
                      title={certificateName}
                    >
                      {certificateName
                        ? certificateName
                        : t("workspace:loading")}
                    </button>
                    <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                    <span className="text-cfTextBreadcrumbActive font-medium">
                      {t("workspace:issuance_summary_data")}
                    </span>
                  </div>
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            <div>
              <h1 className="text-xl font-bold text-cfHeadingText">
                {t("workspace:issuance_summary_data")}
              </h1>
            </div>
          </div>
          <div className="gap-3 flex">
            <button
              className="submit-btn px-4 py-2 text-black border items-center text-center text-md rounded-[8px] focus:outline-none"
              onClick={handleOpenQrEditModal}
              disabled={!firstValidPdfUrl}
            >
              {t("workspace:preview_certificate")}
            </button>
            <button
              className="submit-btn px-4 py-2 bg-cfPrimaryButton text-cfSecondary text-md rounded-[8px] focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
              onClick={() => setIsConfirmModalOpen(true)}
              disabled={loading}
            >
              {t("workspace:issue_certificates_button")}
            </button>
          </div>
        </div>

        {/* Confirmation Modal */}
        {isConfirmModalOpen && (
          <Dialog
            open
            onClose={() => setIsConfirmModalOpen(false)}
            className="relative z-50"
          >
            <div
              className="fixed inset-0 bg-black bg-opacity-60"
              aria-hidden="true"
            />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel className="bg-white rounded-xl max-w-lg w-full transform transition-all duration-300 scale-95 opacity-0 animate-modalOpen">
                <div className="p-4 border-b">
                  <Dialog.Title as="h3" className="text-lg font-semibold">
                    {t("workspace:confirm_certificate_issuance")}
                  </Dialog.Title>
                  <div className="text-sm text-gray-500">
                    {summaryData.metrics.count_of_valid_recipients > 0 && (
                      <span className="font-medium">
                        {t("workspace:you_are_about_to_issue")}{" "}
                        {summaryData.metrics.count_of_valid_recipients}{" "}
                        {t("workspace:certificates_text")}
                      </span>
                    )}
                    <br />
                    {summaryData.metrics.count_of_duplicate_values > 0 && (
                      <div>
                        <span className="font-semibold text-amber-600">
                          {t("workspace:warning")}:{" "}
                          {summaryData.metrics.count_of_duplicate_values}{" "}
                          {t("workspace:already_issued_not_again")}
                        </span>
                        <br />
                        <span>{t("workspace:action_irreversible")}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 text-amber-600 mb-2">
                    <AlertCircle className="h-5 w-5" />
                    <p className="font-medium">
                      {t("workspace:important_information")}
                    </p>
                  </div>
                  <ul className="text-sm text-gray-500 space-y-1 list-disc pl-5">
                    <li>
                      {t("workspace:certificates_issued_valid_recipients")}
                    </li>
                    <li>{t("workspace:email_notifications_if_enabled")}</li>
                    <li>
                      {t("workspace:duplicate_recipients_no_certificates")}
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-t flex justify-end gap-2">
                  <button
                    className="px-4 py-2 w-[7rem] border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={() => setIsConfirmModalOpen(false)}
                  >
                    {t("workspace:cancel")}
                  </button>
                  <button
                    className="px-4 py-2 w-[7rem] bg-cfPrimaryButton text-white rounded-md hover:bg-cfPrimaryDark disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleIssueCertificates}
                    disabled={
                      summaryData.metrics.count_of_valid_recipients === 0 ||
                      loading
                    }
                  >
                    {t("workspace:issue")}
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        )}

        {/* Certificate Preview Modal */}
        {isPreviewModalOpen && (
          <Dialog
            open
            onClose={() => setIsPreviewModalOpen(false)}
            className="relative z-50"
          >
            <div
              className="fixed inset-0 bg-black bg-opacity-60"
              aria-hidden="true"
            />
            <div className="fixed inset-0 flex items-center justify-center p-4">
              <Dialog.Panel className="bg-white rounded-xl max-w-3xl w-full h-[90vh] flex flex-col scale-100 opacity-100 translate-y-0 transform transition-all duration-300 animate-modalOpen">
                <div className="p-4 border-b">
                  <Dialog.Title as="h3" className="text-lg font-semibold">
                    {t("workspace:certificate_preview")}
                  </Dialog.Title>
                  {previewRecipient && (
                    <p className="text-sm text-gray-500">
                      {t("workspace:previewing_certificate_for")}{" "}
                      {previewRecipient.recipientFirstName}{" "}
                      {previewRecipient.recipientLastName}
                    </p>
                  )}
                </div>
                <div className="flex-1 p-4 overflow-auto">
                  {previewRecipient && previewRecipient.legacy_file_url ? (
                    <div className="h-full w-full flex items-center justify-center">
                      <canvas
                        ref={pdfCanvasRef}
                        className="border rounded-md shadow-lg max-w-full max-h-full"
                      />
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center bg-gray-50 rounded-md">
                      <p className="text-gray-500">
                        {t("workspace:no_preview_available")}
                      </p>
                    </div>
                  )}
                </div>
                <div className="p-4 border-t flex justify-end">
                  <button
                    className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={() => setIsPreviewModalOpen(false)}
                  >
                    {t("workspace:close")}
                  </button>
                </div>
              </Dialog.Panel>
            </div>
          </Dialog>
        )}

        {isQrEditModalOpen && (
          <QrEditModal
            isOpen={isQrEditModalOpen}
            onClose={() => setIsQrEditModalOpen(false)}
            qrPosition={qrPosition}
            setQrPosition={setQrPosition}
            qrSize={qrSize}
            setQrSize={setQrSize}
            certificateRef={certificateRef}
            pdfUrl={firstValidPdfUrl}
            issuer_credential_template_id={params.issuer_credential_template_id}
          />
        )}
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 px-4">
          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {t("workspace:total_recipients")}
                </p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.number_of_rows_in_excel}
                </h3>
              </div>
              <div className="bg-indigo-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {t("workspace:valid_recipients")}
                </p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.count_of_valid_recipients}
                </h3>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {t("workspace:duplicate_recipients")}
                </p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.count_of_duplicate_values}
                </h3>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <Copy className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="border rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {t("workspace:certificate_files")}
                </p>
                <h3 className="text-3xl font-bold">
                  {summaryData.metrics.count_of_recipient_files}
                </h3>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Issuance Details */}
        <div className="bg-white border rounded-xl p-6 m-6 mb-6">
          <h2
            id="transaction-issuance-details-heading"
            className="text-lg text-cfSignInSignUpHeadingText font-semibold mb-4"
          >
            {t("workspace:issuance_details")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">
                {t("workspace:issuance_name")}
              </p>
              <p className="font-medium">
                {summaryData.legacy_transcation_data.transaction_name}
              </p>
            </div>
            {summaryData.legacy_transcation_data.description && (
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {t("workspace:description")}
                </p>
                <p>{summaryData.legacy_transcation_data.description}</p>
              </div>
            )}

            <div>
              <p className="text-sm text-gray-500 mb-1">
                {t("workspace:common_column")}
              </p>
              <p className="font-medium">
                {summaryData.legacy_transcation_data.common_column}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">
                {t("workspace:template")}
              </p>
              <p className="font-medium">
                {summaryData.legacy_transcation_data.certificate_name}
              </p>
            </div>
          </div>
        </div>

        {/* Recipients Table */}
        <div className="bg-white border rounded-xl overflow-hidden m-4">
          <div className="p-4 border-b">
            <h2 className="text-lg text-cfSignInSignUpHeadingText font-semibold">
              {t("workspace:recipients")}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cfTableHeaderBackgroundColor text-center h-[3.625rem]">
                <tr>
                  <th className="px-6 py-3 text-xs font-semibold text-cfSignInSignUpInputLabelText uppercase tracking-wider">
                    {t("workspace:file_status")}
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-cfSignInSignUpInputLabelText uppercase tracking-wider">
                    {t("workspace:action_normal")}
                  </th>
                  {Object.keys(summaryData.validated_data[0]).map((key) => {
                    if (
                      key === "is_legacy_file_present" ||
                      key === "legacy_file_url" ||
                      key === "is_duplicate"
                    )
                      return null;
                    const headerText = summaryData.legacy_transcation_data
                      .is_nad
                      ? key.replace(/_/g, " ")
                      : key
                          .replace(/_/g, " ")
                          .replace(/([A-Z])/g, " $1")
                          .replace(/^./, (str) => str.toUpperCase());
                    return (
                      <th
                        key={key}
                        className="px-6 py-3 text-xs font-semibold text-cfSignInSignUpInputLabelText uppercase tracking-wider"
                      >
                        <div
                          className="truncate max-w-[150px] mx-auto"
                          title={headerText}
                        >
                          {headerText}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-center">
                {currentItems.map((recipient, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 h-[3.625rem] mx-4"
                  >
                    <td className="px-4 whitespace-nowrap text-sm">
                      {!recipient.is_duplicate &&
                        recipient.is_legacy_file_present && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />{" "}
                            {t("workspace:valid")}
                          </span>
                        )}
                      {((recipient.is_duplicate &&
                        recipient.is_legacy_file_present) ||
                        (recipient.is_duplicate &&
                          !recipient.is_legacy_file_present)) && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle className="h-3 w-3 mr-1" />{" "}
                          {t("workspace:duplicate")}
                        </span>
                      )}
                      {!recipient.is_duplicate &&
                        !recipient.is_legacy_file_present && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="h-3 w-3 mr-1" />{" "}
                            {t("workspace:unmatched")}
                          </span>
                        )}
                    </td>
                    <td className="px-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => handlePreviewCertificate(recipient)}
                        className={`inline-flex items-center justify-center p-2 rounded-full focus:outline-none ${
                          recipient.is_legacy_file_present
                            ? "text-indigo-600 hover:bg-indigo-50 cursor-pointer"
                            : "text-gray-300 cursor-not-allowed"
                        }`}
                        disabled={!recipient.is_legacy_file_present}
                        title={
                          recipient.is_legacy_file_present
                            ? t("workspace:preview_certificate")
                            : t("workspace:no_certificate_to_preview")
                        }
                        aria-label={
                          recipient.is_legacy_file_present
                            ? `${t("workspace:preview_certificate_for")} ${
                                recipient.recipientFirstName
                              } ${recipient.recipientLastName}`
                            : t("workspace:certificate_preview_not_available")
                        }
                      >
                        <ImageUpscale className="h-5 w-5" />
                      </button>
                    </td>
                    {Object.entries(recipient).map(([key, value]) => {
                      if (
                        key === "is_legacy_file_present" ||
                        key === "legacy_file_url" ||
                        key === "is_duplicate"
                      )
                        return null;
                      return (
                        <td
                          key={key}
                          className="px-4 whitespace-nowrap text-sm text-cfSignInSignUpInputLabelText"
                        >
                          {value || "-"}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 px-4 py-3 border-t">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            >
              <span className="flex items-center justify-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                  />
                </svg>
                <span>{t("workspace:previous")}</span>
              </span>
            </button>

            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            >
              <span className="flex items-center justify-center gap-2">
                <span>{t("workspace:next")}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5 rtl:-scale-x-100"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                  />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
